import React from 'react';
import styled, { css } from 'styled-components';

type Props = {
    value?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    name?: string;
    autoComplete?: string;
    type?: React.HTMLInputTypeAttribute;
    fullWidth?: boolean;
    hasError?: boolean;
    height?: number; // default 38 to match react-select
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    maxLength?: number;
    // Called when cleared/Escape is pressed so can wire into it. e.g. clear a filter completely
    onClear?: () => void;

};

const Control = styled.div<{ $fullWidth?: boolean; $height: number; $disabled?: boolean; $error?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  border: 1px solid hsl(0, 0%, 80%);
  background-color: ${({ $disabled }) => ($disabled ? '#f6f6f6' : '#fff')};
  min-height: ${({ $height }) => $height}px;
  height: ${({ $height }) => $height}px;
  padding: 2px 8px;
  box-sizing: border-box;
  transition: border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease;
  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}

  ${({ $error }) =>
    $error &&
    css`
      border-color: #e24646;
      box-shadow: 0 0 0 1px rgba(226, 70, 70, 0.2);
    `}

  &:focus-within {
    border-color: #000000;
    box-shadow: 0 0 0 1px #000000;
  }
`;

const InputEl = styled.input`
  appearance: none;
  border: none;
  outline: none;
  flex: 1 1 auto;
  font-size: 14px;
  line-height: 1;
  color: #333;
  background: transparent;
  min-width: 0;

  ::placeholder {
    color: #9e9e9e;
  }

  :disabled {
    color: #999;
    cursor: not-allowed;
  }
`;

export const SelectLikeTextInput: React.FC<Props> = ({
    value,
    onChange,
    placeholder,
    disabled,
    name,
    type = 'text',
    autoComplete,
    fullWidth,
    hasError,
    height = 38,
    onBlur,
    onFocus,
    inputMode,
    maxLength,
    onClear,
}) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            // Prevent any parent handlers from also reacting to Escape
            e.preventDefault();
            e.stopPropagation();
            if (onClear) {
                onClear();
            } else {
                // Fallback: clear to empty string if no explicit onClear is provided
                if (value) onChange('');
            }
        }
    };

    return (
        <Control $fullWidth={!!fullWidth} $height={height} $disabled={disabled} $error={hasError}>
            <InputEl
                name={name}
                type={type}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete={autoComplete}
                onBlur={onBlur}
                onFocus={onFocus}
                inputMode={inputMode}
                maxLength={maxLength}
                onKeyDown={handleKeyDown}
            />
        </Control>
    );
};
